import { useLocalization, type Language } from '../../../../../config/localization';
import { getSafeExternalUrl } from '../../../../../utils/safeExternalUrl';
import { WORKFLOW_URL_ROLE_ENTRIES, type WorkflowUrlRole } from '../../../../../utils/workflowUrlRoles';
import CopyValueButton from '../../../../ui/CopyValueButton';
import Icon, { ICONS } from '../../../../ui/icons';
import type { PromptOption } from '../../../../ui/SearchablePromptSelect';

export interface EnhancerEndpointUrl {
  id?: number;
  url_index: number;
  url: string;
  description?: string | null;
  prompt_id?: number | null;
  dify_workflow_url?: string | null;
  api_key?: string | null;
  extra_params?: string | null;
}

interface EnhancerEndpointsSummaryProps {
  urls: EnhancerEndpointUrl[];
  prompts: PromptOption[];
  language: Language;
  loadError?: boolean;
  /**
   * Enables inline editing of the Prompt Enhance endpoint: renders an edit
   * button on the configured card and a configure button on the empty state.
   * The Translate role is deliberately not shown here (deprecated in this view).
   */
  onEditPromptEnhance?: () => void;
}

// This summary intentionally surfaces only the Prompt Enhance endpoint —
// Translate is legacy and stays visible only in the Dify Endpoints section.
const SUMMARY_ROLE_ENTRIES = WORKFLOW_URL_ROLE_ENTRIES.filter(
  ([, role]) => role === 'prompt_enhance',
);

export default function EnhancerEndpointsSummary({
  urls,
  prompts,
  language,
  loadError = false,
  onEditPromptEnhance,
}: EnhancerEndpointsSummaryProps) {
  const t = useLocalization(language);
  const cfg = t.adminWorkflows.urlManager;
  const pipelineCfg = t.admin.pipelineStepsConfig;

  const getRoleLabel = (role: WorkflowUrlRole) => (
    role === 'translate' ? cfg.roleTranslate : cfg.rolePromptEnhance
  );

  return (
    <section className="rounded-lg border border-cyan-800/50 bg-cyan-950/20 p-4" data-testid="enhancer-endpoints-summary">
      <h4 className="text-sm font-semibold text-white">{cfg.enhancerEndpointsTitle}</h4>
      <p className="mt-1 text-xs text-gray-400">{cfg.enhancerEndpointsDesc}</p>

      {loadError && (
        <p className="mt-4 text-sm text-amber-300" role="status">
          {cfg.enhancerEndpointsLoadError}
        </p>
      )}

      <div className="mt-4 space-y-3">
        {!loadError && SUMMARY_ROLE_ENTRIES.map(([urlIndex, role]) => {
          const endpoint = urls.find((url) => url.url_index === urlIndex);
          const safeDifyUrl = getSafeExternalUrl(endpoint?.dify_workflow_url);

          return (
            <div
              key={urlIndex}
              className="rounded border border-gray-700 bg-gray-900/50 p-3"
              data-testid={`enhancer-endpoint-${role}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded border border-cyan-500/30 bg-cyan-900/20 px-2 py-1 text-xs font-medium text-cyan-300">
                  {getRoleLabel(role)}
                </span>
                {!endpoint && <span className="text-xs text-gray-500">{cfg.notConfigured}</span>}
                {onEditPromptEnhance && endpoint && (
                  <button
                    type="button"
                    onClick={onEditPromptEnhance}
                    aria-label={cfg.editUrl}
                    title={cfg.editUrl}
                    className="ml-auto rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                    data-testid="enhancer-endpoint-edit"
                  >
                    <Icon name={ICONS.EDIT} size={14} />
                  </button>
                )}
                {onEditPromptEnhance && !endpoint && (
                  <button
                    type="button"
                    onClick={onEditPromptEnhance}
                    className="ml-auto inline-flex items-center gap-1.5 rounded border border-cyan-500/40 px-2 py-1 text-xs font-medium text-cyan-300 transition-colors hover:bg-cyan-900/30"
                    data-testid="enhancer-endpoint-configure"
                  >
                    <Icon name={ICONS.PLUS} size={12} />
                    {cfg.configureUrl}
                  </button>
                )}
              </div>

              {endpoint && (
                <div className="mt-3 space-y-2 text-xs">
                  {endpoint.description && (
                    <div>
                      <span className="text-gray-500">{cfg.description}: </span>
                      <span className="text-gray-300">{endpoint.description}</span>
                    </div>
                  )}

                  <div className="flex items-start gap-1">
                    <span className="shrink-0 text-gray-500">{cfg.apiUrl}:</span>
                    <span className="min-w-0 break-all font-mono text-gray-300">{endpoint.url}</span>
                    <CopyValueButton
                      value={endpoint.url}
                      title={cfg.copy}
                      copiedTitle={cfg.copied}
                      className="shrink-0"
                    />
                  </div>

                  {endpoint.dify_workflow_url && (
                    <div className="flex items-start gap-1">
                      <span className="shrink-0 text-gray-500">{cfg.difyWorkflowUrl}:</span>
                      <span className="min-w-0 break-all font-mono text-gray-300">
                        {endpoint.dify_workflow_url}
                      </span>
                      <CopyValueButton
                        value={endpoint.dify_workflow_url}
                        title={cfg.copy}
                        copiedTitle={cfg.copied}
                        className="shrink-0"
                      />
                      {safeDifyUrl && (
                        <a
                          href={safeDifyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={cfg.openDifyUrl}
                          title={cfg.openDifyUrl}
                          className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-700 hover:text-blue-400"
                        >
                          <Icon name={ICONS.EXTERNAL_LINK} size={14} />
                        </a>
                      )}
                    </div>
                  )}

                  {endpoint.prompt_id != null && (() => {
                    const prompt = prompts.find((candidate) => candidate.id === String(endpoint.prompt_id));
                    return prompt ? (
                      <div>
                        <span className="text-gray-500">{pipelineCfg.workflowPrompt}: </span>
                        <span className="font-medium text-green-400">{prompt.title}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
